import * as Immutable from 'immutable';
import {WHITE_LABEL} from '../utils/config';
import {isAudioVideoUnsupported, isMobile} from '../utils/helpers';

export enum SegmentType {
    SoftwareEngineering = 'software_engineering',
    ProductManagement = 'product_management',
    CustomerService = 'customer_service',
    Other = 'other',
}

// "PracticeWithFriend" is special because it isn't considered a real
// interview type in the DB.
export enum InterviewTypeName {
    Algorithms = 'algorithms',
    PracticeWithFriend = 'practice-with-friend',
    SystemDesign = 'system-design',
    Frontend = 'front-end',
    Behavioral = 'behavioral',
    AppliedDataScience = 'applied-data-science',
    ProductCaseStudy = 'product-case-study',
    Situational = 'situational',
}

export const INTERVIEW_TYPE_NAMES =
    Immutable.Set<string>(Object.values(InterviewTypeName));

export enum InterviewTypeApiName {
    Algorithms = 'algorithms',
    SystemDesign = 'system_design',
    Frontend = 'frontend',
    Behavioral = 'behavioral',
    AppliedDataScience = 'applied_data_science',
    ProductCaseStudy = 'product_case_study',
    Situational = 'situational',
}

export enum CollabMode {
    Code = 'code',
    Video = 'video',
    Screen = 'screen',
}

export interface InterviewType {
    name: InterviewTypeName;
    apiName: InterviewTypeApiName;
    showQuestionToInterviewee: boolean;
    defaultCollabMode: CollabMode;
    swapRoles: boolean;
    canSwitchCollabMode: boolean;
}

const interviewTypeList: InterviewType[] = [
    {
        name: InterviewTypeName.Algorithms,
        apiName: InterviewTypeApiName.Algorithms,
        showQuestionToInterviewee: true,
        defaultCollabMode: CollabMode.Code,
        swapRoles: true,
        canSwitchCollabMode: true,
    },
    {
        name: InterviewTypeName.Frontend,
        apiName: InterviewTypeApiName.Frontend,
        showQuestionToInterviewee: true,
        defaultCollabMode: CollabMode.Code,
        swapRoles: true,
        canSwitchCollabMode: true,
    },
    {
        name: InterviewTypeName.SystemDesign,
        apiName: InterviewTypeApiName.SystemDesign,
        showQuestionToInterviewee: true,
        defaultCollabMode: CollabMode.Code,
        swapRoles: true,
        canSwitchCollabMode: true,
    },
    {
        name: InterviewTypeName.Behavioral,
        apiName: InterviewTypeApiName.Behavioral,
        showQuestionToInterviewee: false,
        defaultCollabMode: CollabMode.Video,
        swapRoles: true,
        canSwitchCollabMode: true,
    },
    {
        name: InterviewTypeName.AppliedDataScience,
        apiName: InterviewTypeApiName.AppliedDataScience,
        showQuestionToInterviewee: false,
        defaultCollabMode: CollabMode.Code,
        swapRoles: true,
        canSwitchCollabMode: true,
    },
    {
        name: InterviewTypeName.ProductCaseStudy,
        apiName: InterviewTypeApiName.ProductCaseStudy,
        showQuestionToInterviewee: false,
        defaultCollabMode: CollabMode.Video,
        swapRoles: true,
        canSwitchCollabMode: true,
    },
    {
        name: InterviewTypeName.Situational,
        apiName: InterviewTypeApiName.Situational,
        showQuestionToInterviewee: false,
        defaultCollabMode: CollabMode.Video,
        swapRoles: true,
        canSwitchCollabMode: false,
    },
];

export const INTERVIEW_TYPES = Immutable.Map<InterviewTypeName, InterviewType>(interviewTypeList.map(props => (
    [props.name, props] as [InterviewTypeName, InterviewType]
)));
export const INTERVIEW_TYPES_BY_API_NAME = Immutable.Map<InterviewTypeApiName, InterviewType>(interviewTypeList.map(props => (
    [props.apiName, props] as [InterviewTypeApiName, InterviewType]
)));

const interviewTypesBySegment = Immutable.Map<SegmentType, {
    interviewType: InterviewTypeName,
    isNew?: boolean,
}[]>([
    [SegmentType.SoftwareEngineering, [
        {interviewType: InterviewTypeName.Algorithms},
        {interviewType: InterviewTypeName.Behavioral,         isNew: true},
        {interviewType: InterviewTypeName.SystemDesign,       isNew: true},
        {interviewType: InterviewTypeName.Frontend,           isNew: true},
        {interviewType: InterviewTypeName.AppliedDataScience, isNew: true},
        {interviewType: InterviewTypeName.PracticeWithFriend},
    ]],
    [SegmentType.ProductManagement, [
        {interviewType: InterviewTypeName.ProductCaseStudy},
        {interviewType: InterviewTypeName.Behavioral},
    ]],
]);

const greenInterviewTypesBySegment = Immutable.Map<SegmentType, {
    interviewType: InterviewTypeName,
    isNew?: boolean,
}[]>([
    [SegmentType.SoftwareEngineering, [
        {interviewType: InterviewTypeName.Algorithms},
        {interviewType: InterviewTypeName.Behavioral},
        {interviewType: InterviewTypeName.SystemDesign},
    ]],
    [SegmentType.ProductManagement, [
        {interviewType: InterviewTypeName.ProductCaseStudy},
        {interviewType: InterviewTypeName.Behavioral},
    ]],
    [SegmentType.CustomerService, [
        {interviewType: InterviewTypeName.Situational},
    ]],
]);

export const INTERVIEW_TYPES_BY_SEGMENT = WHITE_LABEL === 'green'
    ? greenInterviewTypesBySegment
    : interviewTypesBySegment;

export const isInterviewTypeInSegment = (segment: SegmentType, interviewType: InterviewTypeName) => (
    INTERVIEW_TYPES_BY_SEGMENT.get(segment, [])
        .find(typeForSegment => typeForSegment.interviewType === interviewType)
);

export const shouldSupportForInterviewType = (interviewType: InterviewType): boolean => {
    if (interviewType.name === InterviewTypeName.Situational) {
        return !isAudioVideoUnsupported();
    }
    return !isAudioVideoUnsupported() && !isMobile();
};
